export type CatalogCellValue = string | number | boolean | null;

export type CatalogRow = Record<string, CatalogCellValue> & {
  id: string;
  active: boolean;
  status?: string;
};

export interface CatalogNavItem {
  id: string;
  label: string;
  shortLabel?: string;
  description: string;
  icon: 'home' | 'globe' | 'table' | 'grid' | 'users' | 'settings' | 'package' | 'map-pin';
  removable?: boolean;
  visible?: boolean;
  badgeLabel?: string;
  displayType?: 'AUTO_APPROVE' | 'PARALLEL' | 'HIERARCHY';
  metaChips?: string[];
  statusLabel?: string;
  statusClass?: string;
}

export interface CatalogFilterConfig {
  key: string;
  label: string;
  placeholder: string;
  icon: 'tag' | 'list' | 'user';
}

export interface CatalogTableColumn {
  key: string;
  label: string;
  /** Field name to use in the save payload when it differs from `key`. */
  apiKey?: string;
  type?: 'text' | 'status' | 'currency' | 'date' | 'email' | 'dropdown';
  editable?: boolean;
  width?: string;
  className?: string;
  options?: string[];
}

export interface CatalogEntityDataApi {
  url: string;
  method?: 'get' | 'post';
  payload?: unknown;
  responseMap?: Record<string, string>;
}

/** A tab shown above the catalog table that switches to another entity's view. */
export interface CatalogEntityTab {
  id: string;
  label: string;
}

export interface CatalogEntityConfig {
  id: string;
  title: string;
  navLabel: string;
  navDescription: string;
  shortLabel?: string;
  icon: CatalogNavItem['icon'];
  addButtonLabel: string;
  filterListener: string[];
  filters: CatalogFilterConfig[];
  columns: CatalogTableColumn[];
  newRowDefaults: Partial<CatalogRow>;
  entityName?: string;
  /** Column api keys identifying the record; sent as `businessKey` in the update payload. */
  businessKey?: string[];
  saveOptions?: {
    includeActiveFlag?: boolean;
  };
  dataApi?: CatalogEntityDataApi;
  dynamic?: boolean;
  sidebarCard?: {
    visible?: boolean;
    badgeLabel?: string;
    description?: string;
    displayType?: 'AUTO_APPROVE' | 'PARALLEL' | 'HIERARCHY';
    metaChips?: string[];
    statusLabel?: string;
    statusClass?: string;
  };
  pageHeader?: Record<string, unknown>;
  filterPanel?: Record<string, unknown>;
  table?: Record<string, unknown>;
  /**
   * Sub-views rendered as tabs above the table. Each id references another
   * entity in `entities` (usually hidden from the sidebar via
   * `sidebarCard.visible: false`); selecting this entity forwards to the
   * first tab.
   */
  tabs?: CatalogEntityTab[];
  /** Config for the bespoke DOA setup surface (shown instead of filters + table). */
  doaSetup?: DoaSetupConfig;
}

export interface DoaSetupRangeRow {
  bu: string;
  rangeLevel: number;
  ranges: string;
  faded?: boolean;
}

export interface DoaSetupConfig {
  businessUnitOptions?: string[];
  regionOptions?: string[];
  approvalTypeOptions?: string[];
  rangeLevelOptions?: number[];
  serialOrderOptions?: number[];
  rangeRows?: DoaSetupRangeRow[];
}

/** Dropdown filter rendered in the catalog table's quick-filter row; `key` matches a column key. */
export interface CatalogQuickFilter {
  key: string;
  label: string;
  options: string[];
}

export interface CatalogTypeOption {
  id: string;
  label: string;
  description: string;
}

export type CatalogThemeVariables = Record<`--${string}`, string>;

export interface CatalogThemeConfig {
  light?: CatalogThemeVariables;
  dark?: CatalogThemeVariables;
}

export interface DomainCatalogConfig {
  title: string;
  subtitle: string;
  headerTitle: string;
  initialCatalogId: string;
  apiUrl: string;
  futureApiUrl: string;
  /** Endpoint used to populate the Business Unit dropdown in Add Row forms. */
  businessUnitOptionsApi?: string;
  /** Endpoint used to populate the Region dropdown in Add Row forms. */
  regionOptionsApi?: string;
  /** Endpoint used to populate the Country dropdown in Add Row forms. */
  countryOptionsApi?: string;
  apiConfig?: Record<string, unknown>;
  theme?: CatalogThemeConfig;
  entities: CatalogEntityConfig[];
}

export interface DomainCatalogPayload {
  response?: Array<{
    data?: {
      catalogs?: Record<string, CatalogRow[]>;
    };
  }>;
  responseList?: Array<{
    data?: Array<{
      data?: {
        catalogs?: Record<string, CatalogRow[]>;
      };
    }>;
  }>;
}

export interface DomainCatalogViewModel {
  loading: boolean;
  sidebarCollapsed: boolean;
  activeCatalogId: string;
  config: DomainCatalogConfig | null;
  navItems: CatalogNavItem[];
  rows: CatalogRow[];
  filteredRows: CatalogRow[];
  filters: Record<string, string>;
  selectedRowId: string | null;
  editingIds: Set<string>;
  editValues: Map<string, CatalogRow>;
  /** Ids of locally-added rows awaiting an insert on "Save All". */
  pendingInsertIds: Set<string>;
  /** Ids of rows whose active flag was toggled, awaiting an update on "Save All". */
  pendingToggleIds: Set<string>;
  /** Ids of rows marked for deletion, awaiting a delete (`is_deleted: true`) on "Save All". */
  pendingDeleteIds: Set<string>;
  /**
   * Unsaved change count per catalog id, including catalogs the user has
   * switched away from. Drives the sidebar card pending badges so every card
   * surfaces its own pending work independently.
   */
  pendingCounts: Record<string, number>;
  isAdding: boolean;
  draftRow: CatalogRow | null;
  showAddCatalogModal: boolean;
  /** True while the delete confirmation popup is open for `deleteConfirmRowId`. */
  showDeleteConfirmModal: boolean;
  /** Id of the row awaiting delete confirmation; null when no delete is in progress. */
  deleteConfirmRowId: string | null;
  newCatalogType: string;
  availableCatalogTypes: CatalogTypeOption[];
  toastMessage: string;
  toastVisible: boolean;
  toastVariant: 'success' | 'error';
  isCompact: boolean;
}

/**
 * Counts unique rows with work pending for Save. A row can temporarily appear
 * in more than one staging set (for example, a newly inserted row being
 * edited), but Save should display and report that row only once.
 */
export function getPendingCatalogChangeCount(
  vm: Pick<
    DomainCatalogViewModel,
    'editingIds' | 'pendingInsertIds' | 'pendingToggleIds' | 'pendingDeleteIds'
  >,
): number {
  return new Set([
    ...vm.editingIds,
    ...vm.pendingInsertIds,
    ...vm.pendingToggleIds,
    ...vm.pendingDeleteIds,
  ]).size;
}

/**
 * Maps pending counts from hidden tab entities onto their visible parent card.
 * Tab counts remain available so each tab can restore and display its own
 * pending state, while the parent card shows the combined unsaved total.
 */
export function getPendingCatalogCardCounts(
  counts: Record<string, number>,
  entities: CatalogEntityConfig[],
): Record<string, number> {
  const cardCounts = { ...counts };

  entities.forEach((entity) => {
    if (!entity.tabs?.length) return;

    const tabCount = entity.tabs.reduce((total, tab) => total + (counts[tab.id] || 0), 0);
    const total = (counts[entity.id] || 0) + tabCount;
    if (total) {
      cardCounts[entity.id] = total;
    } else {
      delete cardCounts[entity.id];
    }
  });

  return cardCounts;
}

/**
 * Detects application-level save failures returned with a successful HTTP
 * status. Some catalog APIs report duplicate/constraint errors in the response
 * body instead of returning a non-2xx status, so the save flow must inspect it.
 */
export function isCatalogSaveFailureResponse(response: unknown): boolean {
  const visited = new Set<object>();

  const isFailure = (value: unknown, depth: number): boolean => {
    if (!value || depth > 6) return false;
    if (Array.isArray(value)) return value.some((item) => isFailure(item, depth + 1));
    if (typeof value !== 'object') return false;

    const body = value as Record<string, unknown>;
    if (visited.has(body)) return false;
    visited.add(body);

    if (
      body['success'] === false ||
      body['ok'] === false ||
      body['isSuccess'] === false ||
      body['hasError'] === true
    ) {
      return true;
    }

    const numericStatus = Number(
      body['statusCode'] ?? body['status_code'] ?? body['httpStatus'] ?? body['http_status'],
    );
    if (Number.isFinite(numericStatus) && numericStatus >= 400) return true;

    const status = String(body['status'] ?? body['state'] ?? '').trim().toLowerCase();
    if (['error', 'failed', 'failure', 'duplicate', 'conflict'].includes(status)) return true;

    const explicitError = body['error'] ?? body['errors'] ?? body['errorMessage'] ?? body['error_message'] ?? body['exception'];
    const hasExplicitError =
      explicitError !== undefined &&
      explicitError !== null &&
      explicitError !== false &&
      explicitError !== '' &&
      (Array.isArray(explicitError)
        ? explicitError.length > 0
        : typeof explicitError === 'object'
          ? Object.keys(explicitError as object).length > 0
          : true);
    if (hasExplicitError) {
      return true;
    }

    const message = String(body['message'] ?? body['detail'] ?? '').toLowerCase();
    if (/duplicate|already exists|unique constraint|constraint violation|failed|failure|error/.test(message)) {
      return true;
    }

    return Object.values(body).some((nested) => isFailure(nested, depth + 1));
  };

  return isFailure(response, 0);
}

/**
 * Authoritative success check for a save response. The save APIs return HTTP 200
 * even for business/DB validation failures and signal the outcome through a
 * top-level `error` field instead: `null` (or absent/empty) means the save
 * committed, any value means it was rolled back and carries the reason. Never
 * rely on the HTTP status for these calls.
 *
 * Returns the failure reason when the save failed, or an empty string on success.
 * When the response carries no top-level `error` field (older APIs embedded the
 * failure elsewhere) it falls back to the legacy heuristic scan.
 */
export function getSaveResponseError(response: unknown): string {
  if (response && typeof response === 'object' && !Array.isArray(response) && 'error' in response) {
    const error = (response as Record<string, unknown>)['error'];
    if (error === null || error === undefined || error === '') return '';
    return typeof error === 'string' ? error.trim() : String(error);
  }

  return isCatalogSaveFailureResponse(response) ? getCatalogSaveFailureMessage(response) : '';
}

/**
 * Pulls the most human-readable failure reason out of a save response so the UI
 * can tell the user *why* the save failed (e.g. a duplicate record) instead of
 * a generic message. Returns an empty string when nothing useful is found.
 */
export function getCatalogSaveFailureMessage(response: unknown): string {
  const visited = new Set<object>();

  const readString = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = readString(item);
        if (found) return found;
      }
    }
    return '';
  };

  const find = (value: unknown, depth: number): string => {
    if (!value || depth > 6) return '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = find(item, depth + 1);
        if (found) return found;
      }
      return '';
    }
    if (typeof value !== 'object') return '';

    const body = value as Record<string, unknown>;
    if (visited.has(body)) return '';
    visited.add(body);

    // The explicit error fields carry the most specific reason (DB constraint,
    // duplicate key, etc.), so prefer them before the generic message fields.
    const reason =
      readString(body['errorMessage']) ||
      readString(body['error_message']) ||
      readString(body['error']) ||
      readString(body['errors']) ||
      readString(body['exception']) ||
      readString(body['message']) ||
      readString(body['detail']);
    if (reason) return reason;

    for (const nested of Object.values(body)) {
      const found = find(nested, depth + 1);
      if (found) return found;
    }
    return '';
  };

  return find(response, 0);
}

export interface DuplicateSaveFailure {
  /** Human-readable field label, e.g. "Business Unit". */
  fieldName: string;
  /** The offending value, e.g. "CBU". */
  value: string;
}

/**
 * Detects a duplicate/unique-constraint save failure and extracts the offending
 * field and value so the UI can show a contextual message instead of the raw
 * SQL. Understands the Postgres `Detail: Key (col)=(val) already exists.` clause
 * (including composite keys such as `Key (region, business_unit)=(IN, CBU)`) and
 * falls back to generic "duplicate key"/"unique constraint" wording. Passing the
 * active catalog's `columns` lets the DB field name resolve to its display label.
 * Returns null when the message is not a duplicate/unique-constraint error.
 */
export function parseDuplicateSaveFailure(
  rawMessage: string,
  columns?: CatalogTableColumn[],
): DuplicateSaveFailure | null {
  const message = String(rawMessage ?? '');
  if (!/duplicate key|already exists|unique constraint|violates unique/i.test(message)) {
    return null;
  }

  const detail = message.match(/Key\s*\(([^)]*)\)\s*=\s*\(([^)]*)\)/i);
  const rawFields = detail ? splitDetailList(detail[1]) : [];
  const rawValues = detail ? splitDetailList(detail[2]) : [];

  const fieldName = rawFields.length
    ? rawFields.map((field) => resolveCatalogFieldLabel(field, columns)).join(' and ')
    : '';
  const value = rawValues.join(', ');

  return { fieldName: fieldName || 'value', value };
}

function splitDetailList(segment: string): string[] {
  return segment
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Maps a DB field name to its catalog column label, falling back to a Title Cased name. */
function resolveCatalogFieldLabel(dbField: string, columns?: CatalogTableColumn[]): string {
  const match = columns?.find((column) => (column.apiKey || column.key) === dbField || column.key === dbField);
  if (match?.label) return match.label;
  return dbField
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
